import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, Clock } from "lucide-react";
import { BRAND, IMAGES } from "@/data/content";
import { scrollToHash } from "@/lib/ui";
import { Mark } from "@/components/Motif";
import { EASE } from "@/components/Reveal";

const D = 1.15; // waits for the loader curtain

function Reveal({ children, delay, className = "", y = "112%" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block will-change-transform"
        initial={{ y }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-20"
    >
      <div className="absolute -top-40 right-[-12%] w-[42rem] h-[42rem] rounded-full bg-gold/10 blur-[140px] pointer-events-none" />
      <span
        aria-hidden="true"
        className="pointer-events-none select-none absolute -bottom-8 -left-4 font-bengali text-[24vw] leading-none text-gold/[0.05]"
      >
        মিষ্টি
      </span>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: D - 0.15 }}
            className="flex items-center gap-3 text-gold"
          >
            <Mark className="w-5 h-5" />
            <span className="text-[11px] sm:text-xs uppercase tracking-[0.3em]">
              Pure Desi Ghee <span className="font-bengali normal-case tracking-normal text-sm">খাঁটি দেশি ঘি</span>
            </span>
          </motion.div>

          <h1 className="mt-6 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-cream">
            <Reveal delay={D + 0.05}>Fresh Traditional</Reveal>
            <Reveal delay={D + 0.17}>
              Sweets &amp; <em className="italic font-light text-gold">Morning</em>
            </Reveal>
            <Reveal delay={D + 0.29}>Delights in Banskandi</Reveal>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: D + 0.45 }}
            className="mt-6 max-w-xl text-cream/70 text-base sm:text-lg leading-relaxed"
          >
            Start your day right with hot samosas, fresh rotis with bogni, and our signature
            traditional mithai made fresh daily.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: D + 0.55 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-menu"
              onClick={() => scrollToHash("#menu")}
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold tracking-wide text-ink hover:bg-gold-light transition-colors duration-200"
            >
              View Full Menu
              <ArrowDown className="w-4 h-4 transition-transform duration-200 group-hover:translate-y-0.5" />
            </button>
            <a
              data-testid="hero-cta-call"
              href={`tel:${BRAND.phones[0]}`}
              className="inline-flex items-center gap-3 rounded-full border border-gold/40 px-7 py-3.5 text-sm font-semibold tracking-wide text-gold hover:bg-gold/10 transition-colors duration-200"
            >
              <Phone className="w-4 h-4" />
              Call {BRAND.phones[0]}
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: D + 0.7 }}
            className="mt-9 flex items-center gap-2.5 text-[11px] uppercase tracking-[0.22em] text-cream/50"
          >
            <Clock className="w-4 h-4 text-gold/70" />
            Open Daily {BRAND.hours} · {BRAND.address}
          </motion.p>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: D + 0.3 }}
            className="relative mx-auto w-[82%] sm:w-[68%] lg:w-full max-w-md"
          >
            <div className="rounded-t-[999px] rounded-b-3xl overflow-hidden border border-gold/30 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]">
              <img
                src={IMAGES.heroMain}
                alt="Assorted pure desi ghee mithai at Samij Sweets"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, rotate: 0, y: 24 }}
              animate={{ opacity: 1, rotate: -4, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: D + 0.55 }}
              className="absolute -left-4 sm:-left-12 bottom-6 w-32 sm:w-44 rounded-2xl overflow-hidden border border-gold/40 shadow-[0_24px_50px_-20px_rgba(0,0,0,0.85)]"
            >
              <img
                src={IMAGES.heroOverlap}
                alt="Wrapped celebration platter of assorted sweets"
                className="w-full aspect-square object-cover"
              />
            </motion.div>
          </motion.div>

          <motion.div
            style={{ y: yBadge }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: EASE, delay: D + 0.7 }}
            className="absolute top-2 right-2 sm:right-8 w-24 h-24 sm:w-28 sm:h-28"
          >
            <div className="relative w-full h-full rounded-full bg-cocoa-900/90 border border-gold/30 backdrop-blur-sm">
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-spin-slow text-gold/80">
                <defs>
                  <path id="badge-circle" d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0" />
                </defs>
                <text fill="currentColor" fontSize="8" letterSpacing="2.6" fontFamily="Outfit, sans-serif">
                  <textPath href="#badge-circle">PURE DESI GHEE · MADE FRESH DAILY ·</textPath>
                </text>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <Mark className="w-8 h-8 text-gold" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
